// Business logic constants
import { 
  OVERSEAS_TERRITORIES_DATA, 
  findOverseasTerritoryById, 
  findOverseasTerritoryByPath, 
  getOverseasTerritories,
  OverseasTerritory,
  OverseasTerritoryId
} from '../types';

export const VALIDATION_LIMITS = {
  ACTS_PER_WEEK: {
    MIN: 0,
    MAX: 200
  },
  VACATION: {
    MIN_WEEKS: 0,
    MAX_WEEKS: 20,
    MIN_HOLIDAYS: 0,
    MAX_HOLIDAYS: 15
  },
  RETROCESSION: {
    MIN_PERCENTAGE: 0,
    MAX_PERCENTAGE: 100,
    MIN_CAP: 0
  },
  CHARGES: {
    MIN_PERCENTAGE: 0,
    MAX_PERCENTAGE: 100
  }
};

export const RECOMMENDED_VALUES = {
  VACATION_WEEKS: '5-6 semaines',
  PUBLIC_HOLIDAYS_FRANCE: '8-10 jours',
  CHARGES_PERCENTAGE_RANGE: '40-50%',
  RETROCESSION_PERCENTAGE: 'Variable selon la profession et le statut'
};

export const DEFAULT_VACATION_SETTINGS = {
  vacationWeeks: 0,
  publicHolidays: 8,
  replacementWeeks: 0,
  replacementPercentage: 20
};

export const DEFAULT_STATUS_SETTINGS = {
  type: '' as const,
  retrocessionPercentage: 30,
  retrocessionCap: null,
  fiscalRegime: 'reel' as const
};

export const DEFAULT_CHARGES_SETTINGS = {
  chargesPercentage: 0
};

// Overseas territories majoration
export const OVERSEAS_TERRITORIES_MAJORATION = [
  { id: 'guadeloupe', name: 'Guadeloupe', majoration: 25 },
  { id: 'guyane', name: 'Guyane', majoration: 25 },
  { id: 'martinique', name: 'Martinique', majoration: 25 },
  { id: 'reunion', name: 'La Réunion', majoration: 25 },
  { id: 'saint-barthelemy', name: 'Saint-Barthélemy', majoration: 25 },
  { id: 'saint-martin', name: 'Saint-Martin', majoration: 25 },
  { id: 'saint-pierre-miquelon', name: 'Saint-Pierre-et-Miquelon', majoration: 25 },
  { id: 'mayotte', name: 'Mayotte', majoration: 40 }
] as const;

// Temporary complement for specific overseas territories
export const TEMPORARY_COMPLEMENT_MAJORATION = [
  { id: 'guadeloupe', name: 'Guadeloupe', complement: 15 },
  { id: 'martinique', name: 'Martinique', complement: 15 },
  { id: 'saint-barthelemy', name: 'Saint-Barthélemy', complement: 15 },
  { id: 'saint-martin', name: 'Saint-Martin', complement: 15 },
  { id: 'guyane', name: 'Guyane', complement: 15 },
  { id: 'reunion', name: 'La Réunion', complement: 10 }
] as const;

// DOM-TOM tax abatements for income tax
export const DOM_TOM_TAX_ABATEMENTS = [
  { id: 'guadeloupe', name: 'Guadeloupe', abatementRate: 30, abatementCap: 2450 },
  { id: 'martinique', name: 'Martinique', abatementRate: 30, abatementCap: 2450 },
  { id: 'reunion', name: 'La Réunion', abatementRate: 30, abatementCap: 2450 },
  { id: 'guyane', name: 'Guyane', abatementRate: 40, abatementCap: 4050 },
  { id: 'mayotte', name: 'Mayotte', abatementRate: 40, abatementCap: 4050 }
] as const;

// Articles par profession avec métadonnées complètes
export interface ArticleMetadata {
  url: string;
  title: string;
  description: string;
  image?: string;
  publishedDate?: string;
  readTime?: string;
  category?: string; // Catégorie de l'article (ex: Salaire, Carrière, Formation)
  territories?: string[]; // Territoires spécifiques pour cet article
  grades?: number[]; // Grades spécifiques pour cet article
  echelonRange?: { min: number; max: number }; // Plage d'échelons pour cet article
  contextualIntro?: string; // Introduction contextuelle dynamique
  contextualConclusion?: string; // Conclusion contextuelle dynamique
}

export const PROFESSION_ARTICLES: Record<string, ArticleMetadata[]> = {
  infirmier: [
    {
      url: 'https://appines.fr/guide/emploi/infirmiers/salaire/combien-gagne-un-infirmier',
      title: 'Salaire infirmier : combien gagne un infirmier en France ?',
      description: 'Salaire moyen, primes, évolution et différences entre secteurs : découvre tout sur la rémunération des infirmiers en 2025.',
      readTime: '6 min',
      category: 'Salaire'
    },
    {
      url: 'https://appines.fr/guide/emploi/infirmiers/salaire/hopital-public',
      title: 'Salaire infirmier à l\'hôpital public : grilles et évolution',
      description: 'Découvre les grilles salariales et les primes des infirmiers dans le public selon le grade et l\'ancienneté.',
      readTime: '5 min',
      category: 'Salaire'
    },
    {
      url: 'https://appines.fr/guide/emploi/infirmiers/salaire/infirmier-de-nuit',
      title: 'Salaire infirmier de nuit : combien gagne-t-il vraiment ?',
      description: 'Travailler la nuit, ça paye ? Découvre la rémunération des infirmiers de nuit, les primes et les avantages du travail nocturne.',
      readTime: '6 min',
      category: 'Salaire'
    },
    {
      url: 'https://appines.fr/guide/emploi/infirmiers/salaire/infirmier-coordinateur',
      title: 'Salaire infirmier coordinateur : salaire moyen et évolution',
      description: 'Tout savoir sur le salaire d\'un infirmier coordinateur, selon son expérience, le type d\'établissement et les responsabilités.',
      readTime: '6 min',
      category: 'Salaire'
    },
    {
      url: 'https://appines.fr/guide/emploi/infirmiers/salaire/infirmiere-puericultrice',
      title: 'Salaire infirmière puéricultrice : combien peut-elle espérer ?',
      description: 'Salaire, primes et perspectives d\'évolution : découvre la rémunération d\'une infirmière puéricultrice dans le public et le privé.',
      readTime: '6 min',
      category: 'Salaire'
    },
    {
      url: 'https://appines.fr/guide/emploi/infirmiers/salaire/infirmier-reanimation',
      title: 'Salaire infirmière réanimation : combien gagne-t-elle ?',
      description: 'Découvre la rémunération d\'une infirmière en réanimation : primes, responsabilités et évolution selon l\'expérience.',
      readTime: '6 min',
      category: 'Salaire'
    },
    {
      url: 'https://appines.fr/guide/emploi/infirmiers/salaire/infirmier-urgentiste',
      title: 'Salaire infirmier urgentiste : combien gagne-t-il par mois ?',
      description: 'Découvre le salaire moyen d\'un infirmier urgentiste, les primes et les facteurs qui influencent sa rémunération.',
      readTime: '6 min',
      category: 'Salaire'
    },
    {
      url: 'https://appines.fr/guide/emploi/infirmiers/salaire/ipa',
      title: 'Salaire infirmier en pratique avancée (IPA) : le guide complet',
      description: 'Découvre le salaire d\'un infirmier en pratique avancée, ses missions et les écarts de rémunération selon le secteur.',
      readTime: '6 min',
      category: 'Salaire'
    },
    {
      url: 'https://appines.fr/guide/emploi/infirmiers/salaire/infirmier-militaire',
      title: 'Salaire infirmier militaire : combien gagne-t-il en 2025 ?',
      description: 'Entre solde, primes et avantages, découvre la rémunération complète d\'un infirmier militaire selon son grade et son ancienneté.',
      readTime: '6 min',
      category: 'Salaire'
    }
  ],
  'infirmiere-anesthesiste-iade': [
    {
      url: 'https://appines.fr/guide/emploi/infirmiers/salaire/combien-gagne-un-infirmier',
      title: 'Salaire infirmier : combien gagne un infirmier en France ?',
      description: 'Salaire moyen, primes, évolution et différences entre secteurs : découvre tout sur la rémunération des infirmiers en 2025.',
      readTime: '6 min',
      category: 'Salaire'
    },
    {
      url: 'https://appines.fr/guide/emploi/infirmiers/salaire/hopital-public',
      title: 'Salaire infirmier à l\'hôpital public : grilles et évolution',
      description: 'Découvre les grilles salariales et les primes des infirmiers dans le public selon le grade et l\'ancienneté.',
      readTime: '5 min',
      category: 'Salaire'
    },
    {
      url: 'https://appines.fr/guide/emploi/infirmiers/salaire/infirmier-de-nuit',
      title: 'Salaire infirmier de nuit : combien gagne-t-il vraiment ?',
      description: 'Travailler la nuit, ça paye ? Découvre la rémunération des infirmiers de nuit, les primes et les avantages du travail nocturne.',
      readTime: '6 min',
      category: 'Salaire'
    },
    {
      url: 'https://appines.fr/guide/emploi/infirmiers/salaire/combien-gagne-un-iade',
      title: 'Salaire infirmier anesthésiste (IADE) : combien gagne-t-il ?',
      description: 'Découvre tout sur le salaire d\'un infirmier anesthésiste : grilles, primes et évolutions selon l\'expérience et le lieu d\'exercice.',
      readTime: '6 min',
      category: 'Salaire'
    }
  ],
  'infirmiere-bloc-operatoire-ibode': [
    {
      url: 'https://appines.fr/guide/emploi/infirmiers/salaire/combien-gagne-un-infirmier',
      title: 'Salaire infirmier : combien gagne un infirmier en France ?',
      description: 'Salaire moyen, primes, évolution et différences entre secteurs : découvre tout sur la rémunération des infirmiers en 2025.',
      readTime: '6 min',
      category: 'Salaire'
    },
    {
      url: 'https://appines.fr/guide/emploi/infirmiers/salaire/hopital-public',
      title: 'Salaire infirmier à l\'hôpital public : grilles et évolution',
      description: 'Découvre les grilles salariales et les primes des infirmiers dans le public selon le grade et l\'ancienneté.',
      readTime: '5 min',
      category: 'Salaire'
    },
    {
      url: 'https://appines.fr/guide/emploi/infirmiers/salaire/infirmier-de-nuit',
      title: 'Salaire infirmier de nuit : combien gagne-t-il vraiment ?',
      description: 'Travailler la nuit, ça paye ? Découvre la rémunération des infirmiers de nuit, les primes et les avantages du travail nocturne.',
      readTime: '6 min',
      category: 'Salaire'
    }
  ],
  'infirmiere-puericultrice-ipde': [
    {
      url: 'https://appines.fr/guide/emploi/infirmiers/salaire/combien-gagne-un-infirmier',
      title: 'Salaire infirmier : combien gagne un infirmier en France ?',
      description: 'Salaire moyen, primes, évolution et différences entre secteurs : découvre tout sur la rémunération des infirmiers en 2025.',
      readTime: '6 min',
      category: 'Salaire'
    },
    {
      url: 'https://appines.fr/guide/emploi/infirmiers/salaire/hopital-public',
      title: 'Salaire infirmier à l\'hôpital public : grilles et évolution',
      description: 'Découvre les grilles salariales et les primes des infirmiers dans le public selon le grade et l\'ancienneté.',
      readTime: '5 min',
      category: 'Salaire'
    },
    {
      url: 'https://appines.fr/guide/emploi/infirmiers/salaire/infirmier-de-nuit',
      title: 'Salaire infirmier de nuit : combien gagne-t-il vraiment ?',
      description: 'Travailler la nuit, ça paye ? Découvre la rémunération des infirmiers de nuit, les primes et les avantages du travail nocturne.',
      readTime: '6 min',
      category: 'Salaire'
    },
    {
      url: 'https://appines.fr/guide/emploi/infirmiers/salaire/infirmiere-puericultrice',
      title: 'Salaire infirmière puéricultrice : combien peut-elle espérer ?',
      description: 'Salaire, primes et perspectives d\'évolution : découvre la rémunération d\'une infirmière puéricultrice dans le public et le privé.',
      readTime: '6 min',
      category: 'Salaire'
    }
  ],
  'infirmiere-pratique-avancee-ipa': [
    {
      url: 'https://appines.fr/guide/emploi/infirmiers/salaire/combien-gagne-un-infirmier',
      title: 'Salaire infirmier : combien gagne un infirmier en France ?',
      description: 'Salaire moyen, primes, évolution et différences entre secteurs : découvre tout sur la rémunération des infirmiers en 2025.',
      readTime: '6 min',
      category: 'Salaire'
    },
    {
      url: 'https://appines.fr/guide/emploi/infirmiers/salaire/hopital-public',
      title: 'Salaire infirmier à l\'hôpital public : grilles et évolution',
      description: 'Découvre les grilles salariales et les primes des infirmiers dans le public selon le grade et l\'ancienneté.',
      readTime: '5 min',
      category: 'Salaire'
    },
    {
      url: 'https://appines.fr/guide/emploi/infirmiers/salaire/infirmier-de-nuit',
      title: 'Salaire infirmier de nuit : combien gagne-t-il vraiment ?',
      description: 'Travailler la nuit, ça paye ? Découvre la rémunération des infirmiers de nuit, les primes et les avantages du travail nocturne.',
      readTime: '6 min',
      category: 'Salaire'
    },
    {
      url: 'https://appines.fr/guide/emploi/infirmiers/salaire/ipa',
      title: 'Salaire infirmier en pratique avancée (IPA) : le guide complet',
      description: 'Découvre le salaire d\'un infirmier en pratique avancée, ses missions et les écarts de rémunération selon le secteur.',
      readTime: '6 min',
      category: 'Salaire'
    }
  ],
  medecin: [],
  kinesitherapeute: []
} as const;

// Tax brackets for 2024
export const TAX_BRACKETS_2024 = [
  { min: 0, max: 11497, rate: 0 },
  { min: 11498, max: 29315, rate: 11 },
  { min: 29316, max: 83823, rate: 30 },
  { min: 83824, max: 180294, rate: 41 },
  { min: 180295, max: Infinity, rate: 45 }
];